import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, Landmark, Loader2, ArrowLeft, ShieldCheck, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/molecules/dialog';
import { useEligiblePsps } from '../hooks/use-eligible-psps';
import { useStartOnboarding } from '../hooks/use-start-onboarding';
import { AddPayoutMethodForm } from './add-payout-method-form';
import type { EligiblePspOption, ManualPayoutMethod } from '../types/payout.types';

const PSP_LABELS: Record<string, string> = {
  STRIPE: 'Stripe',
  ADYEN: 'Adyen',
};

interface PayoutOnboardingFlowProps {
  /** Ouverture contrôlée du parcours (déclenchée par « Configurer mon compte »). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Doc 51 Phase 5 — parcours unifié de destination payout (machine à états §C).
 *
 *   Popup 1 (choix PSP) → si CONNECT : Popup 2 (« déjà un compte ? »)
 *                                        ├── OUI  → OAUTH_LINK (Phase 7, bloqué)
 *                                        └── NON  → HOSTED (Express) + 48h
 *                       → si DESTINATION : formulaire IBAN / mobile money (+ MFA)
 *
 * Le clic sur une option = acceptation du consentement (Décision #2) : on passe
 * par `startOnboarding` (qui plie l'attestation consent, Phase 1) AVANT d'ouvrir
 * le formulaire de destination ou de rediriger vers le PSP.
 */
export function PayoutOnboardingFlow({ open, onOpenChange }: PayoutOnboardingFlowProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useEligiblePsps(open);
  const { mutate: startOnboarding, isPending } = useStartOnboarding();
  const [stage, setStage] = useState<'psp' | 'connect'>('psp');
  const [selected, setSelected] = useState<EligiblePspOption | null>(null);
  const [showDestForm, setShowDestForm] = useState(false);
  // Méthodes manuelles disponibles (issues de l'option MANUAL) transmises au form.
  const [destMethods, setDestMethods] = useState<ManualPayoutMethod[] | undefined>(undefined);

  const reset = () => {
    setStage('psp');
    setSelected(null);
  };

  const startError = () =>
    t('payouts.onboarding.startError', {
      defaultValue: 'Impossible de démarrer la configuration du compte de paiement.',
    });

  // Enregistre le consentement + résout le mode côté serveur. HOSTED → redirection
  // PSP ; DESTINATION_REQUIRED → formulaire IBAN / mobile money.
  const runStartOnboarding = () => {
    startOnboarding(`${window.location.origin}/wallet/payouts?onboarding=complete`, {
      onSuccess: (res) => {
        if (res?.onboardingUrl) {
          window.location.href = res.onboardingUrl;
          return;
        }
        if (res?.mode === 'DESTINATION_REQUIRED') {
          onOpenChange(false);
          reset();
          setShowDestForm(true);
          return;
        }
        toast.error(startError());
      },
      onError: (error: unknown) => {
        const r = (error as { response?: { data?: { message?: string | string[] }; message?: string | string[] } })?.response;
        const raw = r?.data?.message ?? r?.message;
        const message = Array.isArray(raw) ? raw.join(', ') : raw || startError();
        toast.error(message);
      },
    });
  };

  const selectOption = (opt: EligiblePspOption) => {
    setSelected(opt);
    if (opt.kind === 'CONNECT') {
      setStage('connect');
    } else {
      setDestMethods(opt.methods);
      runStartOnboarding();
    }
  };

  // Libellé de l'option manuelle selon les méthodes réellement disponibles.
  const manualLabel = (opt: EligiblePspOption): string => {
    const hasIban = !opt.methods || opt.methods.includes('IBAN');
    const hasMobile = !opt.methods || opt.methods.includes('MOBILE_MONEY');
    if (hasIban && hasMobile)
      return t('payouts.psp.manual', 'Virement bancaire / Mobile money');
    if (hasMobile) return t('payouts.psp.manualMobile', 'Mobile money');
    return t('payouts.psp.manualBank', 'Virement bancaire');
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) reset();
          onOpenChange(o);
        }}
      >
        <DialogContent className="sm:max-w-md">
          {stage === 'psp' ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {t('payouts.psp.title', 'Choisissez un moyen de versement')}
                </DialogTitle>
                <DialogDescription>
                  {t(
                    'payouts.psp.description',
                    'Sélectionnez comment vous souhaitez recevoir vos paiements.',
                  )}
                </DialogDescription>
              </DialogHeader>

              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                </div>
              ) : (data?.options?.length ?? 0) === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  {t('payouts.psp.none', 'Aucun moyen de versement disponible pour le moment.')}
                </p>
              ) : (
                <div className="space-y-3">
                  {data?.options.map((opt) => (
                    <button
                      key={`${opt.psp}-${opt.kind}`}
                      type="button"
                      onClick={() => selectOption(opt)}
                      disabled={isPending}
                      className="w-full flex items-center gap-3 p-4 border rounded-lg text-left transition-colors hover:border-primary/50 disabled:opacity-50"
                    >
                      {opt.kind === 'CONNECT' ? (
                        <CreditCard className="size-6 text-blue-600 shrink-0" />
                      ) : (
                        <Landmark className="size-6 text-green-600 shrink-0" />
                      )}
                      <div className="flex-1">
                        <div className="font-medium">
                          {opt.kind === 'CONNECT' ? PSP_LABELS[opt.psp] || opt.psp : manualLabel(opt)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {opt.kind === 'CONNECT'
                            ? t('payouts.psp.connectHint', 'Compte géré, versements automatiques')
                            : t(
                                'payouts.psp.manualHint',
                                'IBAN ou mobile money — vous saisissez vos coordonnées',
                              )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>
                  {t('payouts.connect.title', {
                    defaultValue: 'Compte {{psp}}',
                    psp: PSP_LABELS[selected?.psp ?? ''] || selected?.psp,
                  })}
                </DialogTitle>
                <DialogDescription>
                  {t('payouts.connect.description', 'Avez-vous déjà un compte chez ce prestataire ?')}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3">
                {/* OUI — linking OAuth : Phase 7, non branché */}
                <div className="w-full flex items-center gap-3 p-4 border rounded-lg opacity-60">
                  <div className="flex-1">
                    <div className="font-medium">
                      {t('payouts.connect.yes', 'Oui, lier mon compte existant')}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {t('payouts.connect.yesHint', 'Connexion sécurisée à votre compte.')}
                    </div>
                  </div>
                  <Badge variant="secondary">{t('common.comingSoon', 'Bientôt')}</Badge>
                </div>

                {/* NON — création d'un compte Express (mode HOSTED) */}
                <button
                  type="button"
                  onClick={runStartOnboarding}
                  disabled={isPending}
                  className="w-full flex items-center gap-3 p-4 border rounded-lg text-left transition-colors hover:border-primary/50 disabled:opacity-50"
                >
                  <div className="flex-1">
                    <div className="font-medium">
                      {t('payouts.connect.no', 'Non, créer un compte')}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {t(
                        'payouts.connect.noHint',
                        'Nous créons votre compte ; vous finalisez la vérification.',
                      )}
                    </div>
                  </div>
                  {isPending ? (
                    <Loader2 className="size-5 animate-spin shrink-0" />
                  ) : (
                    <ShieldCheck className="size-5 text-primary shrink-0" />
                  )}
                </button>

                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock className="size-3 shrink-0" />
                  {t(
                    'payouts.connect.deadline',
                    'Un compte non finalisé sous 48 h devra être recréé.',
                  )}
                </div>

                <Button type="button" variant="ghost" onClick={() => setStage('psp')} disabled={isPending}>
                  <ArrowLeft className="size-4 mr-2" />
                  {t('common.back', 'Retour')}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Étape destination (IBAN / mobile money) + MFA — réutilise le form existant. */}
      <AddPayoutMethodForm
        open={showDestForm}
        onOpenChange={setShowDestForm}
        hideTrigger
        methods={destMethods}
      />
    </>
  );
}
